import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function About() {
  const [activeTab, setActiveTab] = useState<'intro' | 'reputation' | 'contact'>('intro');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const tabs = [
    { key: 'intro' as const, label: '品牌介绍', icon: 'ri-building-line' },
    { key: 'reputation' as const, label: '口碑评价', icon: 'ri-thumb-up-line' },
    { key: 'contact' as const, label: '联系我们', icon: 'ri-mail-send-line' },
  ];

  const testimonials = [
    {
      name: '陈小明',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Asian%20man%2C%20simple%20clean%20studio%20portrait%2C%20neutral%20expression%2C%20soft%20lighting%2C%20white%20background%2C%20professional%20look&width=80&height=80&seq=avatar-user-01&orientation=squarish',
      comment: '韩漫屋真的是我见过最棒的韩漫网站！画质清晰，更新速度快，分类也很齐全。每天都要上来刷一刷，已经成了我的日常习惯。',
      rating: 5,
      date: '2026-07-28',
    },
    {
      name: '林雨晴',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Asian%20woman%2C%20simple%20clean%20studio%20portrait%2C%20gentle%20smile%2C%20soft%20lighting%2C%20white%20background%2C%20professional%20look&width=80&height=80&seq=avatar-user-02&orientation=squarish',
      comment: '下拉式阅读体验太流畅了！之前在其他网站需要翻页很麻烦，韩漫屋的设计让我一口气能看几十话，太爽了。',
      rating: 5,
      date: '2026-07-25',
    },
    {
      name: '王浩然',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Asian%20man%20in%20his%2030s%2C%20simple%20clean%20studio%20portrait%2C%20friendly%20smile%2C%20soft%20lighting%2C%20white%20background%2C%20professional%20look&width=80&height=80&seq=avatar-user-03&orientation=squarish',
      comment: '作为韩漫老粉，韩漫屋的资源确实很全。我喜欢看奇幻类和动作类的，这里都有。而且全部免费，真的很良心。推荐给所有喜欢韩漫的朋友！',
      rating: 5,
      date: '2026-07-20',
    },
    {
      name: '张美玲',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Asian%20woman%20with%20warm%20smile%2C%20simple%20clean%20studio%20portrait%2C%20soft%20lighting%2C%20white%20background%2C%20professional%20look&width=80&height=80&seq=avatar-user-04&orientation=squarish',
      comment: '恋爱韩漫重度爱好者！韩漫屋的恋爱类漫画更新特别快，每天晚上睡前必刷。界面干净整洁，没有任何烦人的广告弹窗。',
      rating: 4,
      date: '2026-07-18',
    },
    {
      name: '李俊杰',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20Asian%20man%2C%20simple%20clean%20studio%20portrait%2C%20confident%20look%2C%20soft%20lighting%2C%20white%20background%2C%20professional%20look&width=80&height=80&seq=avatar-user-05&orientation=squarish',
      comment: '在手机上用浏览器看，体验和App一样好！下拉式浏览非常适合手机端。收藏夹功能也很实用，追更必备。强烈推荐！',
      rating: 5,
      date: '2026-07-15',
    },
  ];

  const stats = [
    { value: '5000+', label: '漫画资源', icon: 'ri-book-open-line' },
    { value: '50万+', label: '月活用户', icon: 'ri-user-line' },
    { value: '8', label: '精选分类', icon: 'ri-layout-grid-line' },
    { value: '99.8%', label: '好评率', icon: 'ri-heart-line' },
  ];

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSubmitted(false);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSubmitted(true);
      form.reset();
      return;
    }

    formData.delete('website_alt');

    setFormSubmitting(true);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9ort1aqs38sj6jc7ee0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(Object.fromEntries(formData.entries()) as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (String(msg).includes('spam') || String(msg).includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(String(msg));
        }
      }
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : '网络错误，请稍后重试');
    } finally {
      setFormSubmitting(false);
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫屋',
    url: 'https://hanmanwu.com',
    description: '韩漫屋拥有完整的韩国漫画大全，免费在线观看并采用下拉式界面，呈现好看的韩漫供您探索。',
    email: 'contact@hanmanwu.com',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫屋是什么网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫屋是一个专注于韩国漫画的免费在线阅读平台，提供丰富多样的韩漫资源，采用下拉式阅读界面，无需翻页即可流畅阅读。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫屋的漫画资源有多少？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫屋目前拥有超过5000部韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、耽美、百合等八大分类，每日持续更新。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们"
        description="韩漫屋 - 韩国漫画大全在线观看平台。了解我们的品牌故事、用户口碑评价，以及联系方式。免费韩漫在线阅读，下拉式界面，最好的韩国漫画阅读体验。"
        keywords="韩漫屋, 关于我们, 韩国漫画, 免费韩漫, 联系我们"
        canonicalUrl="https://hanmanwu.com/about"
      />
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={faqSchema} />

      <Navbar />

      <main>
        <div className="pt-20">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
              关于韩漫屋
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              韩漫屋是一个专注于韩国漫画的免费在线阅读平台，致力于为广大中文读者提供最新、最全、最高清的韩漫阅读服务。
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70">
                  <span className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-lg`} />
                  </span>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-900">{stat.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 mb-10 bg-background-100 rounded-full p-1 w-fit mx-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`} />
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'intro' && (
              <div>
                <div className="prose prose-sm max-w-none text-foreground-600">
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mb-4">我们的故事</h2>
                  <p className="mb-4 leading-relaxed">
                    韩漫屋成立于2024年，由一群热爱韩国漫画的年轻人共同创建。我们发现，虽然韩国漫画在全球范围内拥有庞大的粉丝群体，但中文读者在寻找高质量韩漫资源时，往往面临着资源分散、更新缓慢、阅读体验不佳等问题。
                  </p>
                  <p className="mb-4 leading-relaxed">
                    于是，我们决定打造一个专属于中文用户的韩国漫画阅读平台——<strong>韩漫屋</strong>。我们的目标是：让每一位韩漫爱好者都能在这里找到自己喜欢的作品，享受最流畅的阅读体验。
                  </p>
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mb-4 mt-8">我们的特色</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-background-100 rounded-lg">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                        <i className="ri-check-double-line text-primary-500 mr-1.5" />
                        资源丰富
                      </h3>
                      <p className="text-sm text-foreground-500">超过5000部韩国漫画，涵盖八大热门分类，满足不同读者的口味偏好。</p>
                    </div>
                    <div className="p-4 bg-background-100 rounded-lg">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                        <i className="ri-check-double-line text-primary-500 mr-1.5" />
                        完全免费
                      </h3>
                      <p className="text-sm text-foreground-500">所有漫画均免费在线阅读，无需充值、无需会员，真正零门槛。</p>
                    </div>
                    <div className="p-4 bg-background-100 rounded-lg">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                        <i className="ri-check-double-line text-primary-500 mr-1.5" />
                        下拉式阅读
                      </h3>
                      <p className="text-sm text-foreground-500">采用Webtoon风格的下拉式阅读界面，滚动即可阅读，无需翻页，体验如丝般顺滑。</p>
                    </div>
                    <div className="p-4 bg-background-100 rounded-lg">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                        <i className="ri-check-double-line text-primary-500 mr-1.5" />
                        每日更新
                      </h3>
                      <p className="text-sm text-foreground-500">热门连载漫画每日更新，让您第一时间看到最新章节，不错过任何精彩内容。</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reputation' && (
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2 text-center">用户口碑</h2>
                <p className="text-sm text-foreground-500 text-center mb-8">来自真实用户的评价反馈</p>
                <div className="space-y-4">
                  {testimonials.map((t, i) => (
                    <div key={i} className="p-5 bg-background-50 rounded-xl border border-background-200/70">
                      <div className="flex items-start gap-4">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <strong className="text-sm text-foreground-900">{t.name}</strong>
                              <span className="text-xs text-foreground-400">{t.date}</span>
                            </div>
                            <div className="flex items-center gap-0.5">
                              {Array.from({ length: 5 }, (_, j) => (
                                <i
                                  key={j}
                                  className={`text-xs ${j < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="max-w-lg mx-auto">
                <h2 className="text-xl font-bold font-heading text-foreground-900 mb-2 text-center">联系我们</h2>
                <p className="text-sm text-foreground-500 text-center mb-8">
                  有任何问题或建议？欢迎通过以下方式联系我们
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
                  <div className="p-4 bg-background-50 rounded-xl border border-background-200/70 text-center">
                    <span className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg" />
                    </span>
                    <p className="text-xs text-foreground-400">邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">contact@hanmanwu.com</p>
                  </div>
                  <div className="p-4 bg-background-50 rounded-xl border border-background-200/70 text-center">
                    <span className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line text-lg" />
                    </span>
                    <p className="text-xs text-foreground-400">微信</p>
                    <p className="text-sm font-medium text-foreground-900">韩漫屋官方</p>
                  </div>
                  <div className="p-4 bg-background-50 rounded-xl border border-background-200/70 text-center">
                    <span className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-time-line text-lg" />
                    </span>
                    <p className="text-xs text-foreground-400">工作时间</p>
                    <p className="text-sm font-medium text-foreground-900">每日 07:00-23:00</p>
                  </div>
                </div>

                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="space-y-4"
                  noValidate
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formError && (
                    <div className="p-3 bg-primary-50 text-primary-700 text-sm rounded-lg border border-primary-200">
                      {formError}
                    </div>
                  )}

                  {formSubmitted && (
                    <div className="p-3 bg-accent-50 text-accent-700 text-sm rounded-lg border border-accent-200">
                      感谢您的留言！我们会尽快回复您。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formSubmitting ? '发送中...' : '发送留言'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}